<template>
  <div>
    <gc-dashboard-empty-state
      v-if="!transactions.length"
      title="No Transactions"
      message="Currently there are no transactions for this customer. Give them a call and sell them something today!"
      :height="gridHeight"
    />
    <gc-filter-split-view
      v-else
      class="txn-filter-split-view"
      :showLeftContainer="showLeftContainer"
      :showRightContainer="showRightContainer"
      :rightContainerWidth="280"
    >
      <template #leftContent>
        <div class="txn-grid-box">
          <div class="txn-toolbar">
            <span class="txn-toolbar-title">{{ title }}</span>

            <div class="txn-fpills">
              <div
                v-for="pill in filterItems"
                :key="pill.key"
                :class="['txn-fpill', { active: activeFilter === pill.key }]"
                @click="$emit('update:active-filter', pill.key)"
              >
                {{ pill.text }}
              </div>
            </div>

            <div class="txn-toolbar-actions">
              <DxButton
                icon="columnchooser"
                hint="Choose columns"
                styling-mode="text"
                @click="showColumnChooser"
              />
              <DxTextBox
                class="txn-toolbar-search"
                placeholder="Search..."
                value-change-event="input"
                :value="searchText"
                @value-changed="onSearchChanged"
              />
              <DxButton
                v-if="!showRightContainer"
                icon="filter"
                hint="Filters"
                styling-mode="text"
                @click="toggleFilterPanel"
              />
              <DxButton
                v-if="expandable"
                :icon="expanded ? 'collapse' : 'expand'"
                :hint="expanded ? 'Collapse' : 'Full screen'"
                styling-mode="text"
                @click="$emit('toggle-expand')"
              />
            </div>
          </div>

          <DxDataGrid
            ref="grid"
            :data-source="filteredTransactions"
            :show-borders="false"
            :show-column-lines="true"
            :show-row-lines="true"
            :column-auto-width="true"
            :columns="columns"
            :height="gridInnerHeight"
            :column-chooser="{ enabled: true, allowSearch: true, mode: 'select', height: '100%', width: 300 }"
            :search-panel="{ visible: false }"
            key-expr="RecordId"
            @cell-click="onCellClick"
            @cell-prepared="onCellPrepared"
          >
            <DxPaging :enabled="false" />
            <DxPager :visible="false" />
            <DxScrolling mode="virtual" row-rendering-mode="virtual" :preload-enabled="true" />

            <template #transaction-number-cell-template="{ data }">
              <router-link
                v-if="getTransactionLink(data.data)"
                class="txn-link"
                target="_blank"
                :to="getTransactionLink(data.data)"
              >
                {{ data.data.CustomerTransactionNumber }}
              </router-link>
              <span v-else>{{ data.data.CustomerTransactionNumber }}</span>
            </template>

            <template #sales-order-cell-template="{ data }">
              <router-link
                v-if="hasSalesOrderLink(data.data)"
                class="txn-link"
                target="_blank"
                :to="`/sales-order/review/${data.data.SalesOrder}`"
              >
                {{ data.data.SalesOrder }}
              </router-link>
              <span v-else>{{ data.data.SalesOrder }}</span>
            </template>

            <template #status-cell-template="{ data }">
              <gc-status-chip :status="data.data.CustomerTransactionStatus" />
            </template>
          </DxDataGrid>
        </div>

        <div class="txn-foot">
          <span>{{ recordCountSummary }}</span>
          <div class="foot-totals">
            <span>Overdue: <strong class="txn-foot-overdue">{{ formattedOverdueTotal }}</strong></span>
            <span>Net outstanding: <strong>{{ formattedNetOutstandingTotal }}</strong></span>
          </div>
        </div>
      </template>

      <template #rightContent>
        <div class="txn-filter-panel-wrapper">
          <gc-filter-panel
            :toolbarVisible="true"
            :toolbar="filterToolbar"
            :formItemData="filterFormItems"
            :setGridPanelFilter="setGridPanelFilter"
            :gridFoolter="false"
            :from="'model'"
            :isNormal="false"
            :persistenceKey="undefined"
            :overWriteClear="true"
            :heightAdjustment="filterHeightAdjustment"
            ref="gc-filter-panel"
          />
        </div>
      </template>
    </gc-filter-split-view>
  </div>
</template>

<script>
import { DxDataGrid, DxPaging, DxPager, DxScrolling } from "devextreme-vue/data-grid";
import { DxButton } from "devextreme-vue/button";
import { DxTextBox } from "devextreme-vue/text-box";
import GcStatusChip from "../gc-status-chip.vue";
import GcDashboardEmptyState from "./gc-dashboard-empty-state.vue";
import GcFilterSplitView from "../gc-filter-split-view.vue";
import GcFilterPanel from "../gc-filter-panel.vue";
import ColumnWidth from "../../_helpers/data-grid/ColumnWidth";
import { getDecimalFormat } from "../../_store/helpers";

const NEGATIVE_HIGHLIGHT_FIELDS = ["Total", "FXTotal", "Balance", "FXBalance"];

const TOOLBAR_HEIGHT = 40; // height reserved for the custom title/pills/search toolbar row above the grid
const FOOTER_HEIGHT = 28; // height reserved for the record-count/totals footer below the grid

const TYPE_LABELS = {
  Invoice: "invoice",
  Credit: "credit",
  Receipt: "receipt",
  Unallocated: "unallocated receipt",
};

const FILTERS = {
  all: () => true,
  open: (row) => row.CustomerTransactionStatus === "Open",
  overdue: (row) => row.CustomerTransactionStatus === "Overdue",
  "inv-cr": (row) =>
    row.CustomerTransactionType === "Invoice" || row.CustomerTransactionType === "Credit",
  unalloc: (row) => row.CustomerTransactionType === "Unallocated",
};

export const TRANSACTION_FILTER_ITEMS = [
  { key: "all", text: "All" },
  { key: "open", text: "Open" },
  { key: "overdue", text: "Overdue" },
  { key: "inv-cr", text: "Invoices / Credits" },
  { key: "unalloc", text: "Unallocated Receipts" },
];

export default {
  name: "GcDashboardTransactionsCard",
  components: {
    DxDataGrid,
    DxPaging,
    DxPager,
    DxScrolling,
    DxButton,
    DxTextBox,
    GcStatusChip,
    GcDashboardEmptyState,
    GcFilterSplitView,
    GcFilterPanel,
  },
  props: {
    transactions: {
      type: Array,
      required: true,
    },
    activeFilter: {
      type: String,
      default: "all",
    },
    gridHeight: {
      type: [Number, String],
      default: 360,
    },
    title: {
      type: String,
      default: "",
    },
    expandable: {
      type: Boolean,
      default: false,
    },
    expanded: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      showLeftContainer: true,
      showRightContainer: false,
      searchText: "",
      filterToolbar: {
        close: {
          icon: "showpanel",
          onClick: () => {
            this.showRightContainer = !this.showRightContainer;
          },
        },
      },
    };
  },
  computed: {
    filterItems() {
      return TRANSACTION_FILTER_ITEMS;
    },
    gridInnerHeight() {
      return Math.max(Number(this.gridHeight) - TOOLBAR_HEIGHT - FOOTER_HEIGHT, 0);
    },
    decimalFormat() {
      return getDecimalFormat("Decimal-16-2").ReadOnlyFormat;
    },
    exchangeRateFormat() {
      return getDecimalFormat("Decimal-6-4").ReadOnlyFormat;
    },
    transactionCurrencyCode() {
      return (this.transactions[0] && this.transactions[0].Currency) || "";
    },
    baseCurrencyCode() {
      return (this.transactions[0] && this.transactions[0].FXCurrency) || "";
    },
    filteredTransactions() {
      return this.transactions.filter(FILTERS[this.activeFilter]);
    },
    overdueTotal() {
      return this.transactions
        .filter((row) => row.CustomerTransactionStatus === "Overdue")
        .reduce((sum, row) => sum + (row.Balance || 0), 0);
    },
    netOutstandingTotal() {
      return this.transactions.reduce((sum, row) => sum + (row.Balance || 0), 0);
    },
    formattedOverdueTotal() {
      return this.formatCurrency(this.overdueTotal);
    },
    formattedNetOutstandingTotal() {
      return this.formatCurrency(this.netOutstandingTotal);
    },
    recordCountSummary() {
      const counts = this.filteredTransactions.reduce((acc, row) => {
        const label = TYPE_LABELS[row.CustomerTransactionType] || row.CustomerTransactionType;
        acc[label] = (acc[label] || 0) + 1;
        return acc;
      }, {});

      const parts = Object.keys(counts).map((label) => {
        const count = counts[label];
        return `${count} ${label}${count === 1 ? "" : "s"}`;
      });

      return parts.length ? parts.join(" · ") : "No transactions";
    },
    filterHeightAdjustment() {
      return Number(this.gridHeight);
    },
    filterFormItems() {
      return [
        {
          screenField: "CustomerTransactionNumber",
          filterLabel: "Transaction",
          filterIndex: 0,
        },
        {
          screenField: "Code",
          filterDataField: "SalesOrder",
          filterLabel: "Sales Order",
          filterIndex: 1,
          // NOTE: the CustomerTransactionHistory ScreenLayout declares this as
          // type: "Integer", but SalesOrder is a string field on this entity
          // (CustomerTransactionHistory.cs) and filtering here runs client-side
          // against the in-memory array, so a numeric editor risks a
          // number/string mismatch. Text keeps the comparison reliable.
          type: "Text",
        },
        {
          screenField: "CustPO",
          filterIndex: 2,
          type: "Text",
        },
        {
          screenField: "DateIn",
          filterIndex: 3,
        },
        {
          screenField: "DateCreated",
          filterIndex: 4,
        },
        {
          screenField: "DueDate",
          filterDataField: "InvoiceHeaderDateDue",
          filterIndex: 5,
        },
        {
          screenField: "CustomerTransactionType",
          filterIndex: 6,
        },
        {
          screenField: "CustomerTransactionStatus",
          filterIndex: 7,
        },
        {
          screenField: "Currency",
          filterDataField: "Currency",
          filterIndex: 8,
        },
      ];
    },
    columns() {
      return [
        {
          caption: "Date",
          dataField: "DateIn",
          dataType: "date",
          format: "dd MMM yyyy",
          width: ColumnWidth.Date.width,
          minWidth: ColumnWidth.Date.minWidth,
          allowSearch: false,
        },
        {
          caption: "Type",
          dataField: "CustomerTransactionType",
          allowSearch: false,
        },
        {
          caption: "Due Date",
          dataField: "InvoiceHeaderDateDue",
          dataType: "date",
          format: "dd MMM yyyy",
          width: ColumnWidth.Date.width,
          minWidth: ColumnWidth.Date.minWidth,
          allowSearch: false,
        },
        {
          caption: "Transaction",
          dataField: "CustomerTransactionNumber",
          cellTemplate: "transaction-number-cell-template",
          width: ColumnWidth.TransactionNo.width,
          minWidth: ColumnWidth.TransactionNo.minWidth,
        },
        {
          caption: "Sales Order",
          dataField: "SalesOrder",
          cellTemplate: "sales-order-cell-template",
          width: ColumnWidth.SalesOrderNumber.width,
          minWidth: ColumnWidth.SalesOrderNumber.minWidth,
        },
        {
          caption: "Currency",
          dataField: "Currency",
          width: ColumnWidth.CurrencyCode.width,
          minWidth: ColumnWidth.CurrencyCode.minWidth,
          allowSearch: false,
        },
        {
          caption: "FX Rate",
          dataField: "FXRate",
          alignment: "right",
          format: this.exchangeRateFormat,
          width: 75,
          minWidth: 75,
          allowSearch: false,
        },
        {
          caption: `Total (${this.baseCurrencyCode})`,
          dataField: "Total",
          alignment: "right",
          format: this.decimalFormat,
          width: ColumnWidth.Amount.width,
          minWidth: ColumnWidth.Amount.minWidth,
          allowSearch: false,
        },
        {
          caption: `Total (${this.transactionCurrencyCode})`,
          dataField: "FXTotal",
          alignment: "right",
          format: this.decimalFormat,
          width: ColumnWidth.Amount.width,
          minWidth: ColumnWidth.Amount.minWidth,
          allowSearch: false,
        },
        {
          caption: `Balance (${this.baseCurrencyCode})`,
          dataField: "Balance",
          alignment: "right",
          format: this.decimalFormat,
          customizeText: (e) => (e.value === 0 ? "–" : e.valueText),
          width: ColumnWidth.Amount.width,
          minWidth: ColumnWidth.Amount.minWidth,
          allowSearch: false,
        },
        {
          caption: `Balance (${this.transactionCurrencyCode})`,
          dataField: "FXBalance",
          alignment: "right",
          format: this.decimalFormat,
          customizeText: (e) => (e.value === 0 ? "–" : e.valueText),
          width: ColumnWidth.Amount.width,
          minWidth: ColumnWidth.Amount.minWidth,
          allowSearch: false,
        },
        {
          caption: "Status",
          dataField: "CustomerTransactionStatus",
          cellTemplate: "status-cell-template",
          width: ColumnWidth.Status.width,
          minWidth: ColumnWidth.Status.minWidth,
          allowSearch: false,
        },
      ];
    },
  },
  watch: {
    gridHeight() {
      setTimeout(() => {
        if (this.$refs.grid) this.$refs.grid.instance.updateDimensions();
      }, 100);
    },
  },
  methods: {
    formatCurrency(value) {
      return `$${Number(value || 0).toLocaleString("en-AU", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`;
    },
    hasSalesOrderLink(row) {
      return !!row.SalesOrder && row.SalesOrder !== "0";
    },
    getTransactionLink(row) {
      if (row.CustomerTransactionType === "Credit" && row.ICFlag === "C") {
        return `/credit-invoice/review/${row.CustomerTransactionNumber}`;
      }
      if (row.CustomerTransactionType === "Credit" && row.ICFlag === "I") {
        return `/credit-customer/review/${row.CustomerTransactionNumber}`;
      }
      if (row.CustomerTransactionType === "Invoice") {
        return `/invoice/review/${row.CustomerTransactionNumber}`;
      }
      if (row.CustomerTransactionType === "Receipt" || row.CustomerTransactionType === "Unallocated") {
        return `/customer-receipt/review/${row.CustomerTransactionNumber}`;
      }
      return null;
    },
    onCellClick(e) {
      if (e.rowType !== "data") {
        return;
      }

      if (e.column.dataField === "SalesOrder" && this.hasSalesOrderLink(e.data)) {
        return;
      }

      if (e.column.dataField === "CustomerTransactionNumber" && this.getTransactionLink(e.data)) {
        return;
      }

      this.$emit("row-click", e.data);
    },
    onCellPrepared(e) {
      if (e.rowType !== "data" || !NEGATIVE_HIGHLIGHT_FIELDS.includes(e.column.dataField)) {
        return;
      }

      if (e.data[e.column.dataField] < 0) {
        e.cellElement.classList.add("negative-decimal");
      }
    },
    toggleFilterPanel() {
      this.showRightContainer = !this.showRightContainer;
    },
    showColumnChooser() {
      const dataGrid = this.$refs.grid && this.$refs.grid.instance;
      if (!dataGrid) return;

      dataGrid.showColumnChooser();
    },
    onSearchChanged(e) {
      this.searchText = e.value;
      const dataGrid = this.$refs.grid && this.$refs.grid.instance;
      if (!dataGrid) return;

      dataGrid.searchByText(e.value || "");
    },
    // eslint-disable-next-line no-unused-vars
    setGridPanelFilter(e, valueChanged) {
      const dataGrid = this.$refs.grid && this.$refs.grid.instance;
      if (!dataGrid) return;

      if (e && e.length > 0) {
        dataGrid.filter(e);
      } else {
        dataGrid.clearFilter();
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.txn-grid-box {
  border: 1px solid var(--base-border-color, #e0e0e0);
  border-radius: 4px;
  overflow: hidden;

  ::v-deep .dx-datagrid-header-panel {
    display: none;
  }
}

.txn-toolbar {
  display: grid;
  grid-template-columns: auto 1fr auto;
  align-items: center;
  gap: 12px;
  padding: 8px 12px;
  border-bottom: 1px solid var(--base-border-color, #edf0f3);
  flex-wrap: wrap;
}

.txn-toolbar-title {
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.5px;
  white-space: nowrap;
  justify-self: start;
}

.txn-toolbar-actions {
  display: flex;
  align-items: center;
  gap: 4px;
  justify-self: end;
}

.txn-toolbar-search {
  width: 160px;
}

.txn-fpills {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 6px;
  justify-self: center;
}

.txn-fpill {
  padding: 4px 12px;
  border: 1px solid #dee2e6;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 500;
  color: #495057;
  cursor: pointer;
  user-select: none;
  transition: background 0.12s, border-color 0.12s, color 0.12s;
  white-space: nowrap;

  &:hover {
    border-color: #03a9f4;
    color: #0369a1;
  }

  &.active {
    background: #03a9f4;
    border-color: #03a9f4;
    color: #fff;
  }
}

.txn-link {
  color: #35baf6;
  text-decoration: none;
}

.txn-foot {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
  font-size: 11px;
  color: #667085;
  padding: 6px 2px 0;
}

.foot-totals {
  display: flex;
  gap: 18px;
}

.txn-foot-overdue {
  color: #d92d20;
}
</style>
